/**
 * KAN-702: материалы оператора (раньше "Вложения к инструкциям").
 * KAN-721: файлы, которые бот и менеджер отправляют клиентам: прайс-лист,
 * каталоги, фото товаров. Предупреждение, когда не отмечен основной прайс.
 */
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  useInstructionAttachmentMutations,
  useInstructionAttachments,
} from '@/hooks/useInstructionAttachments';
import { AddInstructionAttachmentForm } from './AddInstructionAttachmentForm';
import { InstructionAttachmentRow } from './InstructionAttachmentRow';

interface InstructionAttachmentsProps {
  operatorId: string;
}

export function InstructionAttachments({ operatorId }: InstructionAttachmentsProps): JSX.Element {
  const { data: items = [], isLoading, isError, isSuccess } = useInstructionAttachments(operatorId);
  const { create, update, remove } = useInstructionAttachmentMutations(operatorId);
  const hasMainPrice = items.some(item => item.is_main_price);

  return (
    <div data-testid="instruction-attachments" className="space-y-2">
      <Label>Материалы оператора</Label>
      <p className="text-xs text-muted-foreground">
        Файлы, которые бот и менеджер отправляют клиентам: прайс-лист, каталоги, фото товаров
      </p>

      {isSuccess && !hasMainPrice && (
        <div
          role="status"
          data-testid="no-main-price-warning"
          className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-2 text-xs text-yellow-900">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Бот без прайс-листа: отметьте основной прайс, чтобы бот отправлял его на вопросы о
            ценах и прикладывал к письмам.
          </span>
        </div>
      )}

      {isLoading && <Loader2 aria-label="Загрузка" className="h-4 w-4 animate-spin" />}
      {isError && <p className="text-xs text-destructive">Не удалось загрузить материалы</p>}

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map(item => (
            <InstructionAttachmentRow
              key={item.id}
              item={item}
              onUpdate={data => update.mutateAsync({ id: item.id, ...data })}
              onDelete={() => remove.mutate(item.id)}
            />
          ))}
        </ul>
      )}

      <AddInstructionAttachmentForm
        isSaving={create.isPending}
        onSave={data => create.mutateAsync(data)}
      />
    </div>
  );
}

export default InstructionAttachments;
